import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ContentRow from "./components/ContentRow";
import ContentModal from "./components/ContentModal";
import CurrentlyWatching from "./components/CurrentlyWatching";
import AdminPage from "./components/AdminPage";
import AuthPage from "./components/AuthPage";
import UserDashboard from "./components/UserDashboard";
import UserProfile from "./components/UserProfile";
import PublicProfile from "./components/PublicProfile";
import VerifyEmail from "./components/VerifyEmail";
import { Toaster } from "./components/ui/sonner";
import {
  fetchTrending,
  fetchMoviesByCategory,
  fetchTVByCategory,
  fetchMoviesByGenre,
  fetchAllTVShows,
  fetchAllMovies,
  fetchNewAndPopular,
} from "./services/tmdb";
import { getImageUrl } from "./services/tmdb";

function HomePage({ onSearch, myList, onToggleMyList, user }) {
  const [selectedContent, setSelectedContent] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [filteredContent, setFilteredContent] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  const handleSearch = (results, query) => {
    setSearchResults(results);
    setSearchQuery(query);
    if (query) {
      setActiveFilter(null);
    }
    onSearch(results, query);
  };

  const handleFilter = async (filter) => {
    setActiveFilter(filter);
    setSearchQuery('');
    setSearchResults([]);
    
    if (!filter) {
      setFilteredContent([]);
      return;
    }

    setFilterLoading(true);
    try {
      let content = [];
      switch (filter) {
        case 'tv':
          content = await fetchAllTVShows();
          break;
        case 'movies':
          content = await fetchAllMovies();
          break;
        case 'new':
          content = await fetchNewAndPopular();
          break;
        case 'mylist':
          content = myList || [];
          break;
        default:
          content = [];
      }
      setFilteredContent(content);
    } catch (error) {
      console.error('Error fetching filtered content:', error);
      setFilteredContent([]);
    }
    setFilterLoading(false);
  };

  const handleSelectContent = (content) => {
    setSelectedContent(content);
  };

  const handleCloseModal = () => {
    setSelectedContent(null);
  };

  const getFilterTitle = () => {
    switch (activeFilter) {
      case 'tv': return 'TV Shows';
      case 'movies': return 'Movies';
      case 'new': return 'New & Popular';
      case 'mylist': return 'My List';
      default: return '';
    }
  };

  return (
    <div className="App bg-black min-h-screen">
      <Header 
        onSearch={handleSearch} 
        onFilter={handleFilter}
        activeFilter={activeFilter}
        user={user}
      />

      {/* Search Results View */}
      {searchQuery && searchResults.length > 0 ? (
        <div className="pt-24 px-8 md:px-16 lg:px-24">
          <h2 className="text-2xl font-semibold text-white mb-6">
            Search Results for "{searchQuery}"
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 pb-12">
            {searchResults.map((item) => {
              const posterUrl = getImageUrl(item.poster_path);
              const title = item.title || item.name;

              return (
                <div
                  key={item.id}
                  onClick={() => handleSelectContent(item)}
                  className="cursor-pointer transform transition-all duration-300 hover:scale-110"
                >
                  <div className="relative rounded overflow-hidden shadow-lg">
                    <img
                      src={posterUrl}
                      alt={title}
                      className="w-full h-72 object-cover"
                      onError={(e) => {
                        e.target.src = "/placeholder-movie.svg";
                      }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <p className="text-white text-sm font-semibold p-3 line-clamp-2">
                        {title}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : activeFilter ? (
        /* Filtered Content View */
        <div className="pt-24 px-8 md:px-16 lg:px-24">
          <h2 className="text-2xl font-semibold text-white mb-6">
            {getFilterTitle()}
          </h2>
          {filterLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : filteredContent.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 pb-12">
              {filteredContent.map((item) => {
                const posterUrl = getImageUrl(item.poster_path);
                const title = item.title || item.name;

                return (
                  <div
                    key={item.id}
                    onClick={() => handleSelectContent(item)}
                    className="cursor-pointer transform transition-all duration-300 hover:scale-110"
                  >
                    <div className="relative rounded overflow-hidden shadow-lg">
                      <img
                        src={posterUrl}
                        alt={title}
                        className="w-full h-72 object-cover"
                        onError={(e) => {
                          e.target.src = "/placeholder-movie.svg";
                        }}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <p className="text-white text-sm font-semibold p-3 line-clamp-2">
                          {title}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              {activeFilter === 'mylist' ? (
                <>
                  <p className="text-xl mb-2">Your list is empty</p>
                  <p className="text-sm">Click the thumbs up on any movie or show to add it to your list</p>
                </>
              ) : (
                <p className="text-xl">No content found</p>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Default Home View */
        <>
          <Hero onSelectContent={handleSelectContent} />

          {/* Currently Watching Section - appears above Trending Now */}
          <CurrentlyWatching user={user} onSelectContent={handleSelectContent} />

          <div className="relative space-y-8 pb-12 pt-8">
            <ContentRow
              title="Trending Now"
              fetchFunction={() => fetchTrending("all", "week")}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Popular on Casitaplex"
              fetchFunction={() => fetchMoviesByCategory("popular")}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Top Rated Movies"
              fetchFunction={() => fetchMoviesByCategory("top_rated")}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Action Movies"
              fetchFunction={() => fetchMoviesByGenre(28)}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Comedy Movies"
              fetchFunction={() => fetchMoviesByGenre(35)}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Horror Movies"
              fetchFunction={() => fetchMoviesByGenre(27)}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Romance Movies"
              fetchFunction={() => fetchMoviesByGenre(10749)}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Documentaries"
              fetchFunction={() => fetchMoviesByGenre(99)}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="TV Shows Airing Today"
              fetchFunction={() => fetchTVByCategory("airing_today")}
              onSelectContent={handleSelectContent}
            />
            <ContentRow
              title="Top Rated TV Shows"
              fetchFunction={() => fetchTVByCategory("top_rated")}
              onSelectContent={handleSelectContent}
            />
          </div>
        </>
      )}

      <ContentModal
        content={selectedContent}
        isOpen={!!selectedContent}
        onClose={handleCloseModal}
        user={user}
      />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('casitaplex-user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [myList, setMyList] = useState([]);

  // Check for logged-in user on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('casitaplex-user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    // Load My List from localStorage
    const savedMyList = localStorage.getItem('casitaplex-mylist');
    if (savedMyList) {
      setMyList(JSON.parse(savedMyList));
    }
  }, []);

  // Save My List to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('casitaplex-mylist', JSON.stringify(myList));
  }, [myList]);

  const handleSearch = (results, query) => {
    // Handle search at app level if needed
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('casitaplex-user');
  };

  const handleToggleMyList = (content) => {
    const contentId = content.id;
    const mediaType = content.media_type || (content.title ? 'movie' : 'tv');
    
    setMyList(prevList => {
      const exists = prevList.find(item => item.id === contentId);
      if (exists) {
        // Remove from list
        return prevList.filter(item => item.id !== contentId);
      } else {
        // Add to list with full content data
        return [...prevList, { 
          ...content, 
          media_type: mediaType,
          poster_path: content.poster_path,
          backdrop_path: content.backdrop_path,
          title: content.title,
          name: content.name,
          overview: content.overview,
          vote_average: content.vote_average
        }];
      }
    });
  };

  return (
    <Router>
      <Toaster position="bottom-right" theme="dark" richColors />
      <Routes>
        <Route 
          path="/" 
          element={
            user
              ? <UserDashboard user={user} onLogout={handleLogout} />
              : <AuthPage onLogin={handleLogin} />
          } 
        />
        <Route 
          path="/login" 
          element={
            user ? <Navigate to="/" /> : <AuthPage onLogin={handleLogin} />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            user ? (
              <UserDashboard user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" />
            )
          } 
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route 
          path="/browse" 
          element={
            <HomePage 
              onSearch={handleSearch}
              user={user}
              myList={myList}
              onToggleMyList={handleToggleMyList}
            />
          } 
        />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/profile" element={user ? <UserProfile user={user} /> : <Navigate to="/login" />} />
        <Route path="/user/:username" element={<PublicProfile user={user} />} />
        <Route 
          path="/play" 
          element={
            user ? (
              <UserDashboard user={user} onLogout={handleLogout} autoOpenGame={true} />
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;
